import { Link } from "react-router-dom";
import { ScrollReveal } from "../hooks/useScrollReveal";
import { Shield, ArrowLeft } from "lucide-react";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-cyan-400 no-underline mb-6"><ArrowLeft className="w-3 h-3" /> Back</Link>
        <ScrollReveal>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-6 h-6 text-cyan-400" />
            <h1 className="font-outfit text-2xl font-semibold text-foreground">Privacy Policy</h1>
          </div>
          <p className="text-xs text-muted-foreground mb-8">Effective: January 1, 2026 &middot; Last updated: April 17, 2026</p>
        </ScrollReveal>

        <div className="bg-card border border-border/60 rounded-2xl p-8 space-y-8 text-sm text-foreground/80 leading-relaxed">
          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">1. Introduction</h2>
            <p>App-Mi.ai ("we", "us", "our") operates the App-Mi.ai platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service. We are committed to protecting your privacy and handling your data transparently.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">2. Data We Collect</h2>
            <p className="mb-2">We collect the following categories of data:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>Account Information:</strong> Name, email address, password (hashed)</li>
              <li><strong>Profile Information:</strong> Username, display name, bio, avatar</li>
              <li><strong>Conversations:</strong> Messages exchanged with your AI assistants (encrypted at rest)</li>
              <li><strong>Social Content:</strong> Posts, comments, likes, follows</li>
              <li><strong>AI Configuration:</strong> Assistant settings, custom instructions, AI model preferences</li>
              <li><strong>Usage Data:</strong> Login timestamps, feature usage, interaction patterns</li>
              <li><strong>Device Information:</strong> Browser type, IP address (for security purposes)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">3. How We Use Your Data</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>Provide and maintain our AI assistant services</li>
              <li>Personalize your experience through AI memory (with your consent)</li>
              <li>Process and display your social content (posts, comments)</li>
              <li>Send notifications about activity on your account</li>
              <li>Improve platform functionality and user experience</li>
              <li>Ensure security and prevent abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">4. AI Data Processing</h2>
            <p className="mb-2">When you chat with AI assistants, your messages are processed by third-party AI inference providers:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>OpenRouter:</strong> Routes conversations to the free AI model you select (e.g., Llama, DeepSeek, Qwen, Mistral, Kimi, GLM, GPT-OSS)</li>
              <li><strong>Google (Gemini):</strong> Processes conversations using Gemini models</li>
              <li><strong>Groq:</strong> Provides high-speed inference for open-weight models</li>
            </ul>
            <p className="mt-2"><strong>PII Protection:</strong> Before sending any message to AI providers, our system automatically scans for Personally Identifiable Information (PII) including emails, phone numbers, social security numbers, credit card numbers, and medical information. Detected PII is tokenized (replaced with placeholder tokens) before transmission. AI providers never see your raw personal data.</p>
            <p className="mt-2"><strong>No Training:</strong> Your conversations are not used to train AI models. AI providers process your data solely to generate responses.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">5. Data Storage & Encryption</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>All conversations are encrypted using <strong>AES-256-GCM</strong> encryption at rest</li>
              <li>Data is stored in MongoDB with encryption at rest</li>
              <li>PII token mappings are stored encrypted in a separate collection</li>
              <li>AI memories are encrypted before storage</li>
              <li>Passwords are hashed using bcrypt (never stored in plaintext)</li>
              <li>All data transfers use HTTPS with TLS 1.2+</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">6. Your Rights (GDPR)</h2>
            <p className="mb-2">Under the General Data Protection Regulation (GDPR), you have the following rights:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>Right of Access:</strong> Request a copy of all your data (Settings &rarr; Privacy &rarr; Download My Data)</li>
              <li><strong>Right to Rectification:</strong> Update your profile and account information at any time</li>
              <li><strong>Right to Erasure:</strong> Delete your account and all associated data (Settings &rarr; Privacy &rarr; Delete Account)</li>
              <li><strong>Right to Data Portability:</strong> Export your data in machine-readable JSON format</li>
              <li><strong>Right to Restriction of Processing:</strong> Disable AI learning and data sharing in privacy settings</li>
              <li><strong>Right to Object:</strong> Opt out of marketing communications and data sharing</li>
              <li><strong>Right to Withdraw Consent:</strong> Update your consent preferences at any time without affecting prior processing</li>
            </ul>
            <p className="mt-2">To exercise these rights: go to Settings &rarr; Privacy, or contact us at <strong>privacy@app-mi.ai</strong>.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">7. HIPAA Compliance</h2>
            <p className="mb-2">While App-Mi.ai is not a covered entity under HIPAA, we implement HIPAA-aligned safeguards for any Protected Health Information (PHI) that users may share:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>PHI Detection:</strong> Our system automatically detects medical terms, conditions, and medication names in conversations</li>
              <li><strong>Tokenization:</strong> Detected PHI is tokenized before being sent to AI providers</li>
              <li><strong>Data Isolation:</strong> AI memories and conversation data are strictly isolated per user</li>
              <li><strong>Audit Logging:</strong> All data access and AI interactions are logged for compliance</li>
              <li><strong>Minimum Necessary:</strong> Only data required for service operation is processed</li>
            </ul>
            <p className="mt-2"><strong>Important:</strong> App-Mi.ai AI assistants are not medical professionals. Do not rely on AI responses for medical decisions.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">8. Data Retention</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>Active account data is retained while your account is active</li>
              <li>Deleted data is permanently removed within 30 days</li>
              <li>Audit logs are retained for 7 years per compliance requirements</li>
              <li>Anonymized usage analytics may be retained indefinitely</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">9. Third-Party Services</h2>
            <p>We use the following third-party services to provide our platform:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>OpenRouter:</strong> AI inference routing for free open-weight models — <a href="https://openrouter.ai/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-400">Privacy Policy</a></li>
              <li><strong>Google:</strong> AI model provider (Gemini) — <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-400">Privacy Policy</a></li>
              <li><strong>Groq:</strong> AI inference provider for open-weight models — <a href="https://groq.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-cyan-400">Privacy Policy</a></li>
            </ul>
            <p className="mt-2">We do not sell your data to advertisers or any other third parties.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">10. Children's Privacy</h2>
            <p>App-Mi.ai is not intended for users under the age of 13. We do not knowingly collect data from children under 13. If you believe a child has provided us with personal data, please contact us.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes via email or in-app notification. Continued use of the platform after changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">12. Contact Us</h2>
            <p>For privacy-related questions or to exercise your data rights:</p>
            <p className="mt-1"><strong>Email:</strong> privacy@app-mi.ai</p>
            <p><strong>Data Protection Officer:</strong> dpo@app-mi.ai</p>
          </section>
        </div>
      </div>
    </div>
  );
}
